"use client";

import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Button } from "@offstreetparking/design-system/forms";
import { GoogleIcon } from "@offstreetparking/design-system/icons/GoogleIcon";
import { Text } from "@offstreetparking/design-system/typography";
import { readSignupPrefill } from "@offstreetparking/portal-flow/lib/signup-prefill";
import { useMutation } from "@tanstack/react-query";
import { type OAuthProvider, getProviderLoginUrl, loginWithProvider } from "~/app/[locale]/login/actions";
import { useCompany } from "~/components/providers/CompanyProvider";
import { useLoginPopup } from "./useLoginPopup";

const LOGIN_PROVIDERS: { provider: OAuthProvider; icon: typeof GoogleIcon; label: MessageDescriptor }[] = [
	{ provider: "GoogleOAuth", icon: GoogleIcon, label: msg`Continue with Google` },
];

export const ProviderLoginButtons = ({ redirectTo, onSignedIn }: { redirectTo?: string; onSignedIn?: () => void }) => {
	const { i18n, t } = useLingui();
	const { company, isFromSubdomain } = useCompany();
	const { run } = useLoginPopup({ onSignedIn });

	const {
		mutate: login,
		isPending,
		isError,
		variables: pendingProvider,
	} = useMutation({
		mutationFn: async (provider: OAuthProvider) => {
			const params = {
				provider,
				companyId: company?.id,
				subdomain: isFromSubdomain ? (company?.subdomain ?? undefined) : undefined,
				redirectTo,
				permitPublicId: readSignupPrefill().connectPermitPublicIdentifier,
			};

			await run({
				getPopupUrl: (popupRedirectTo) => getProviderLoginUrl({ ...params, redirectTo: popupRedirectTo }),
				redirectPage: () => loginWithProvider(params),
			});
		},
	});

	if (!company?.isCrmEnabled) {
		return null;
	}

	return (
		<>
			{isError && (
				<div className="rounded-md bg-critical-faded p-4">
					<Text size="sm" className="text-critical">
						{t`Something went wrong. Please try again.`}
					</Text>
				</div>
			)}

			{LOGIN_PROVIDERS.map(({ provider, icon, label }) => (
				<Button
					key={provider}
					intent="secondary"
					type="button"
					className="w-full"
					leftIcon={icon}
					isLoading={isPending && pendingProvider === provider}
					isDisabled={isPending}
					onClick={() => login(provider)}
				>
					{i18n._(label)}
				</Button>
			))}
		</>
	);
};
